import React from 'react';
import { Flex, Box, TextV2 } from '@withjoy/joykit';
import { Close, Restart } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { PRICE_RANGE, INDEPENDENT_BRAND, formatPriceWithCurrency } from '../../../../constants';
import type { Filters } from '@shared/types/Accommodations.types';
import { DEFAULT_FILTERS } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsConsoleSearchParamsState';

type ResetFiltersHeaderProps = Readonly<{
  filters: Filters;
  updateFilters: (filters: Filters) => void;
  currencyCode: string;
  navigatorLanguage: string;
  dynamicPriceMax: number;
}>;

type FilterTagProps = Readonly<{
  label: string;
  onRemove: () => void;
}>;

const FilterTag = ({ label, onRemove }: FilterTagProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      paddingX={5}
      paddingY={3}
      backgroundColor="mono2"
      borderRadius="full"
    >
      <TextV2 typographyVariant="label2">{label}</TextV2>
      <Box
        as="button"
        onClick={onRemove}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRemove();
          }
        }}
        display="flex"
        alignItems="center"
        aria-label={tq`Remove ${label} filter`}
        role="button"
        tabIndex={0}
      >
        <Close size={14} />
      </Box>
    </Box>
  );
};

export const ResetFiltersHeader = ({ filters, updateFilters, currencyCode, navigatorLanguage, dynamicPriceMax }: ResetFiltersHeaderProps) => {
  const { tq } = useTranslation('accommodationsConsole');

  const getPropertyCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      Hotel: tq`Hotel`,
      Other: tq`Other`,
      VacationRental: tq`Vacation Rental`,
    };
    return labels[category] ?? category;
  };

  const hasPriceFilter =
    filters?.priceRange && (filters.priceRange[0] !== PRICE_RANGE.MIN || (filters.priceRange[1] !== dynamicPriceMax && filters.priceRange[1] !== PRICE_RANGE.MAX));

  const priceFilterLabel = (() => {
    if (!hasPriceFilter || !filters?.priceRange) return '';
    const minFormatted = formatPriceWithCurrency(filters.priceRange[0], currencyCode, navigatorLanguage);
    const maxFormatted = formatPriceWithCurrency(filters.priceRange[1], currencyCode, navigatorLanguage);
    return tq`Price: ${minFormatted} - ${maxFormatted}`;
  })();
  const hasFacilitiesFilter = filters?.facilities && filters.facilities.length > 0;

  const hasStarRating = filters?.starRating?.length > 0;
  const hasGuestRating = filters?.userRating?.length > 0;
  const hasPropertyCategory = filters?.propertyCategories?.length > 0;
  const hasFreeCancellation = !!filters?.freeCancellation;
  const hasBrands = filters?.brands?.length > 0;
  const hasWalkToVenue = !!filters?.walkToVenue;
  const hasAnyFilter = hasPriceFilter || hasFacilitiesFilter || hasStarRating || hasGuestRating || hasPropertyCategory || hasFreeCancellation || hasBrands || hasWalkToVenue;

  if (!hasAnyFilter) {
    return null;
  }

  return (
    <Flex
      gap={2}
      flexWrap="wrap"
      paddingX={4}
      paddingY={2}
      justifyContent="space-between"
    >
      <Flex
        gap={2}
        flexWrap="wrap"
      >
        {hasPriceFilter && filters?.priceRange && (
          <FilterTag
            label={priceFilterLabel}
            onRemove={() => updateFilters({ ...filters, priceRange: [PRICE_RANGE.MIN, PRICE_RANGE.MAX] })}
          />
        )}
        {hasStarRating && filters?.starRating && (
          <FilterTag
            label={tq`Star Rating: ${filters.starRating.join(', ')}`}
            onRemove={() => updateFilters({ ...filters, starRating: [] })}
          />
        )}
        {hasGuestRating && filters?.userRating && (
          <FilterTag
            label={tq`Guest Rating: ${filters.userRating.map(r => `${r}+`).join(', ')}`}
            onRemove={() => updateFilters({ ...filters, userRating: [] })}
          />
        )}
        {filters?.facilities?.map(facility => (
          <FilterTag
            key={facility}
            label={facility}
            onRemove={() =>
              updateFilters({
                ...filters,
                facilities: filters.facilities.filter(f => f !== facility),
              })
            }
          />
        ))}
        {hasPropertyCategory && (
          <FilterTag
            label={filters.propertyCategories.map(getPropertyCategoryLabel).join(', ')}
            onRemove={() => updateFilters({ ...filters, propertyCategories: [] })}
          />
        )}
        {hasFreeCancellation && (
          <FilterTag
            label={tq`Free Cancellation`}
            onRemove={() => updateFilters({ ...filters, freeCancellation: false })}
          />
        )}
        {hasWalkToVenue && (
          <FilterTag
            label={tq`Walk to venue`}
            onRemove={() => updateFilters({ ...filters, walkToVenue: false })}
          />
        )}
        {filters?.brands?.map(brand => (
          <FilterTag
            key={brand}
            label={brand === INDEPENDENT_BRAND ? tq`Independent` : brand}
            onRemove={() =>
              updateFilters({
                ...filters,
                brands: filters.brands.filter(b => b !== brand),
              })
            }
          />
        ))}
      </Flex>
      {hasAnyFilter && (
        <Box
          as="button"
          display="flex"
          alignItems="center"
          onClick={() => updateFilters(DEFAULT_FILTERS)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              updateFilters(DEFAULT_FILTERS);
            }
          }}
          gap={1}
          paddingX={5}
          paddingY={3}
          backgroundColor="mono2"
          borderRadius="full"
          cursor={'pointer'}
          aria-label={tq`Reset all filters`}
          role="button"
          tabIndex={0}
        >
          <Box
            display="flex"
            alignItems="center"
          >
            <Restart size={16} />
          </Box>
          <TextV2 typographyVariant="label2">{tq`Reset All`}</TextV2>
        </Box>
      )}
    </Flex>
  );
};
